import React from 'react';

interface PersonalityStepProps {
    data: {
        personality: {
            tone: string;
            formality: string;
            responseLength: string;
        };
    };
    updateData: (data: Partial<any>) => void;
}

const PersonalityStep: React.FC<PersonalityStepProps> = ({ data, updateData }) => {
    const handlePersonalityChange = (key: string, value: string) => {
        updateData({
            personality: {
                ...data.personality,
                [key]: value
            }
        });
    };

    // Beispielantworten basierend auf Persönlichkeitseinstellungen
    const getExampleResponse = () => {
        const { tone, formality, responseLength } = data.personality;

        let response = '';

        // Tone beeinflusst die Grundstimmung der Nachricht
        if (tone === 'friendly') {
            response = formality === 'formal'
                ? 'Hallo! Ich freue mich, dass Sie unsere Website besuchen. Wie kann ich Ihnen heute helfen?'
                : 'Hallo! Schön, dass du da bist. Wie kann ich dir heute helfen?';
        } else if (tone === 'professional') {
            response = formality === 'formal'
                ? 'Guten Tag. Willkommen auf unserer Website. Wie kann ich Ihnen behilflich sein?'
                : 'Guten Tag. Willkommen auf unserer Website. Wie kann ich dir behilflich sein?';
        } else if (tone === 'casual') {
            response = formality === 'formal'
                ? 'Hi! Was kann ich für Sie tun?'
                : 'Hey! Was kann ich für dich tun?';
        } else if (tone === 'enthusiastic') {
            response = formality === 'formal'
                ? 'Fantastisch, dass Sie hier sind! Wie kann ich Ihnen weiterhelfen?'
                : 'Super, dass du hier bist! Wie kann ich dir weiterhelfen?';
        }

        // Bei detaillierteren Antworten erweitern wir den Text
        if (responseLength === 'detailed') {
            response += formality === 'formal'
                ? ' Ich stehe Ihnen für alle Fragen zu unseren Produkten, Services oder anderen Anliegen zur Verfügung. Zögern Sie nicht, zu fragen!'
                : ' Ich bin hier, um dir bei Fragen zu unseren Produkten, Services oder anderen Themen zu helfen. Frag einfach los!';
        } else if (responseLength === 'moderate' && response.length < 80) {
            response += formality === 'formal'
                ? ' Ich beantworte gerne Ihre Fragen.'
                : ' Ich beantworte gerne deine Fragen.';
        }

        // Emoji für bestimmte Stile hinzufügen
        if (tone === 'friendly' || tone === 'enthusiastic') {
            response += ' 😊';
        }

        return response;
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-medium text-gray-900">Persönlichkeit</h2>
            <p className="text-gray-500">
                Definiere die Persönlichkeit und den Kommunikationsstil deines Chatbots. Diese Einstellungen beeinflussen,
                wie der Chatbot mit Benutzern interagiert.
            </p>

            <div>
                <label htmlFor="tone" className="block text-sm font-medium text-gray-700">Tonfall</label>
                <select
                    id="tone"
                    name="tone"
                    value={data.personality.tone}
                    onChange={(e) => handlePersonalityChange('tone', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="friendly">Freundlich</option>
                    <option value="professional">Professionell</option>
                    <option value="casual">Locker</option>
                    <option value="enthusiastic">Enthusiastisch</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                    Der Tonfall bestimmt die grundlegende Stimmung der Antworten deines Chatbots.
                </p>
            </div>

            <div>
                <label htmlFor="formality" className="block text-sm font-medium text-gray-700">Formalität</label>
                <div className="mt-1 flex items-center space-x-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="formality"
                            value="formal"
                            checked={data.personality.formality === 'formal'}
                            onChange={() => handlePersonalityChange('formality', 'formal')}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Formell (Sie-Form)</span>
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="formality"
                            value="casual"
                            checked={data.personality.formality === 'casual'}
                            onChange={() => handlePersonalityChange('formality', 'casual')}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Informell (Du-Form)</span>
                    </label>
                </div>
            </div>

            <div>
                <label htmlFor="responseLength" className="block text-sm font-medium text-gray-700">Antwortlänge</label>
                <select
                    id="responseLength"
                    name="responseLength"
                    value={data.personality.responseLength}
                    onChange={(e) => handlePersonalityChange('responseLength', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="concise">Kurz und knapp</option>
                    <option value="moderate">Mittellang</option>
                    <option value="detailed">Ausführlich</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                    Die Länge der Antworten bestimmt, wie ausführlich dein Chatbot auf Fragen antwortet.
                </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
                <h3 className="text-blue-800 font-semibold mb-2">Beispielantwort:</h3>
                <p className="text-gray-700 p-3 bg-white rounded border border-gray-200">
                    {getExampleResponse()}
                </p>
            </div>
        </div>
    );
};

export default PersonalityStep;